import React from 'react';
import { compose, prop } from 'ramda';
import { branch, renderComponent } from 'recompose';
import { useDeviceSelector } from 'js/store/hooks';
import { usePortletTranslations } from 'js/components/system/Portlet';
import StartStatus from '../common/StartStatusBadge';
import JoinButton from '../common/JoinButton';
import FadeInContainer from '../common/FadeInContainer';
import Prizes from './Prizes';
import Competitors from './CompetitorsTable';
import RaceRules from './RaceRules';
import RaceInfo, { RaceCountDown } from './RaceInfo';
import DetailsFooter from './DetailsFooter';
import RaceInfoList from './RaceInfo/RaceInfoList';
import Games from './Games';
import withConditionalLayouts from './withConditionalLayouts';
import style from './index.scss';

export const Details = (props) => {
    const {
        details,
        displayPrizes,
        displayTable,
        detailsRef,
        containerClassName,
        prizesView,
        raceInfoView,
        displayGames,
        displayCountdown
    } = props;
    const { status, name, rules, leaderboardRules, isRegInternal, joined } = details;
    const t = usePortletTranslations();

    return (
        <FadeInContainer className={`${style.container} ${containerClassName}`} setRef={detailsRef}>
            <div className={style.fixedContainer}>
                <div className={`${style.section} ${style.top}`}>
                    <StartStatus status={status} view="onPage" />
                    <div className={style.name}>
                        <span className={style.text}>{name}</span>
                    </div>
                    <JoinButton {...details} />
                    {isRegInternal && !joined && <div className={style.internalRaceHint}>{t('internalRace.hint')}</div>}
                </div>
                {(displayPrizes || displayTable) && (
                    <div className={`${style.section} ${style.competitorsAndPrizes}`}>
                        <Prizes className={style.prizes} {...{ displayPrizes, prizesView }} {...details} />
                        <Competitors className={style.competitors} displayTable={displayTable} {...details} />
                    </div>
                )}
                <div className={`${style.section} ${style.raceRulesAndInfo}`}>
                    <RaceRules className={style.rules} raceRules={rules} />
                    <RaceInfo
                        {...details}
                        className={style.info}
                        view={raceInfoView}
                        displayCountdown={displayCountdown} />
                </div>
                {displayGames && <div className={`${style.section} ${style.gamesSection}`}>
                    <Games rules={leaderboardRules} />
                </div>}
                <DetailsFooter className={`${style.section} ${style.footer}`} {...details} />
            </div>
        </FadeInContainer>
    );
};

export const DetailsForMobile = (props) => {
    const {
        details,
        displayPrizes,
        displayTable,
        displayCountdown,
        displayGames
    } = props;
    const { name, rules, leaderboardRules, isRegInternal, joined } = details;
    const raceInfoClassName = [
        style.raceInfo,
        !displayTable && style.topBorder,
        !rules && style.bottomBorder
    ].filter(Boolean).join(' ');
    const t = usePortletTranslations();

    return (
        <FadeInContainer className={style.container}>
            <div className={style.section}>
                <StartStatus {...details} view="onPage" />
                <div className={style.name}>
                    <span className={style.text}>{name}</span>
                </div>
                <JoinButton {...details} />
                {isRegInternal && !joined && <div className={style.internalRaceHint}>{t('internalRace.hint')}</div>}
                {displayCountdown && <RaceCountDown {...details} />}
                <Prizes className={style.prizes} displayPrizes={displayPrizes} {...details} />
                <Competitors className={style.competitors} displayTable={displayTable} {...details} />
                <div className={raceInfoClassName}>
                    <RaceInfoList {...details} />
                </div>
                <RaceRules raceRules={rules} />
                {displayGames && <Games rules={leaderboardRules} className={style.gamesContainer} />}
                <DetailsFooter {...details} />
            </div>
        </FadeInContainer>
    );
};

export default compose(
    branch(
        () => useDeviceSelector(prop('isMobile')),
        renderComponent(DetailsForMobile)
    ),
    withConditionalLayouts
)(Details);
